import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLoader } from "components/common/feedback/PageLoader";
import { PageError } from "components/common/feedback/PageError";
import { FilterPanel } from "components/common/FilterPanel";
import { DocumentationGrid } from "components/Documentation/DocumentationGrid";
import { useDocumentationsPage } from "hooks/useDocumentationsPage";
import {
  DOCUMENTATION_STATUSES,
  DOCUMENTATION_STATUS_LABEL_KEYS,
} from "types/documentation";
import type { DocumentationGridProps } from "components/Documentation/DocumentationGrid/types";

const EMPTY_ARRAY: DocumentationGridProps["documentations"] = [];

const DocumentationsPage = () => {
  const { id: projectId } = useParams();
  const { t } = useTranslation();

  const STATUS_OPTIONS = useMemo(
    () =>
      DOCUMENTATION_STATUSES.map((status) => ({
        value: status,
        label: t(DOCUMENTATION_STATUS_LABEL_KEYS[status]),
      })),
    [t],
  );

  const {
    data: documentations,
    error,
    isLoading,
    statusFilter,
    searchTerm,
    setSearchTerm,
    setStatusFilter,
    onLoadMore,
    canLoadMore,
    resetList,
  } = useDocumentationsPage({ projectId: projectId || "" });

  if ((!documentations && isLoading) || !projectId) {
    return <PageLoader message={t("documentation.loading")} />;
  }

  if (error) {
    return (
      <PageError
        message={t("documentation.error_loading")}
        messageDetail={t("documentation.error_retry")}
      />
    );
  }

  return (
    <div className="px-6">
      <FilterPanel
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        statuses={STATUS_OPTIONS}
        onRefresh={resetList}
        searchPlaceholder={t("documentation.search_documentation")}
        allStatusesLabel={t("documentation.all_statuses")}
      />
      <DocumentationGrid
        projectId={projectId}
        documentations={(documentations as any) ?? EMPTY_ARRAY}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
        canLoadMore={canLoadMore}
      />
    </div>
  );
};

export default DocumentationsPage;
